import { useSearchParams } from 'react-router-dom';
import { usePermission, useAnyPermission } from '../../store/auth';
import { useBadges } from '../../hooks/useBadges';
import { cn } from '../../ui';
import { TerritoriesPage } from '../masters/TerritoriesPage';
import { TerritoryCoveragePage } from './TerritoryCoveragePage';

/**
 * Unified Territories page (Tailwind tabs — no antd):
 *  - Structure — build/edit the geography hierarchy (Zone → State → District → Beat).
 *  - Coverage — bind Sales Heads / Sales Persons to nodes and track covered vs unassigned beats.
 * Both tabs render the existing pages embedded (their own antd trees/modals). The active tab is in the URL
 * (?tab=), so the old /masters/territory-coverage link can deep-link straight to the Coverage tab.
 */
export function TerritoryPage() {
  const canStructure = usePermission('TERRITORY.VIEW');
  const canCoverage = useAnyPermission(['TERRITORY.ASSIGN', 'TERRITORY.ASSIGN.HEAD', 'TERRITORY.COVERAGE']);
  const badges = useBadges();
  // The badge is a SUM of two unrelated backlogs, which is why it used to read as arbitrary sitting
  // over an empty coverage-grants table. Both halves now have their own panel on the Coverage tab
  // (UnassignedBeatsCard and CoverageManagerCard's alert), and the hover here names the split so the
  // number is traceable to them before you even click.
  const unassigned = badges.territoriesUnassigned ?? 0;
  const needCover = badges.coverageNeeded ?? 0;
  const coverageBadge = unassigned + needCover;
  const coverageWhy = [
    unassigned ? `${unassigned} ${unassigned === 1 ? 'beat has' : 'beats have'} no sales person` : null,
    needCover ? `${needCover} sales ${needCover === 1 ? 'person is' : 'people are'} away today with no cover` : null,
  ].filter(Boolean).join(' + ');

  const tabs = [
    ...(canStructure ? [{ key: 'structure', label: 'Structure', badge: 0, why: '' }] : []),
    ...(canCoverage ? [{ key: 'coverage', label: 'Coverage', badge: coverageBadge, why: coverageWhy }] : []),
  ];

  const [sp, setSp] = useSearchParams();
  const tabParam = sp.get('tab');
  const active = tabs.some((t) => t.key === tabParam) ? tabParam : tabs[0]?.key;
  const setTab = (k: string) => setSp((prev) => { const n = new URLSearchParams(prev); n.set('tab', k); return n; }, { replace: true });

  return (
    <div>
      <h1 className="mb-2 type-page-title text-content">Territories</h1>

      {/* Tab bar (Tailwind) */}
      <div className="mb-3 flex gap-1 border-b border-line">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'relative -mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 type-body-medium transition-colors',
              active === t.key
                ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
                : 'border-transparent text-muted hover:text-brand-700 dark:hover:text-brand-300',
            )}
          >
            {t.label}
            {t.badge > 0 && (
              <span
                title={t.why ? `${t.why} — both listed on this tab` : undefined}
                className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger-500 px-1 type-caption-strong text-white"
              >
                {t.badge > 99 ? '99+' : t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {active === 'structure' && <TerritoriesPage embedded />}
      {active === 'coverage' && <TerritoryCoveragePage embedded />}
    </div>
  );
}
